import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { BRAND_CODES } from 'brand-config';
import { getBrandDomain, getBrandVaultProject } from './utils';

const selectBrandState = (state: RootState) => state.brand;

export const selectBrandCode = createSelector(
  [selectBrandState],
  (brandState) => brandState.code,
);

/**
 * Minor Defense is a parent/guardian-enrolled product, so several flows drop the
 * member's own PII (date of birth, SSN) and re-word their headings for the guardian.
 */
export const selectIsDminor = createSelector(
  [selectBrandCode],
  (brandCode) => brandCode === BRAND_CODES.minorDefense,
);

/**
 * Financial Shield is the only brand whose AOL / partner enrollment screen collects an SSN, and
 * it authorizes a different legal entity than the other brand on that screen
 * (aol-single-page-enrollment.component.html:167, :272).
 */
export const selectIsFinancialShield = createSelector(
  [selectBrandCode],
  (brandCode) => brandCode === BRAND_CODES.financialShield,
);

export const selectBrandName = createSelector(
  [selectBrandState],
  (brandState) => brandState.name,
);

export const selectCompanyDetails = createSelector(
  [selectBrandState],
  (brandState) => brandState.companyDetails,
);

/**
 * Whether company details are in the store — and, when a `companyCode` is given, whether they are
 * the details for *that* company. The brand slice is persisted, so an `id` alone can be a previous
 * client's response; every caller that routes on the payload must pass the code it asked for.
 *
 * Plain function rather than `createSelector`: the argument is per-caller and the result is a
 * boolean, so there is nothing to memoise and no referential identity to preserve.
 */
export const selectIsCompanyDetailsExist = (
  state: RootState,
  companyCode?: string,
) => {
  const { id, code } = selectCompanyDetails(state);

  if (!id) {
    return false;
  }

  /* The endpoint echoes the code back in mixed case ('Bcph'). */
  return companyCode ? code.toLowerCase() === companyCode.toLowerCase() : true;
};

export const selectBrandCustomerServicePhone = createSelector(
  [selectBrandState, selectCompanyDetails],
  (brandState, companyDetails) => {
    return (
      companyDetails?.altCustomerServiceNumber ||
      companyDetails?.customerCareNumber ||
      brandState.customerServicePhone ||
      ''
    );
  },
);

/**
 * Whether the brand in the store is the one this host should be showing. The slice is persisted,
 * so having an `id` alone is not enough — details fetched under a previous domain would be reused
 * on the next one, and the member would see another brand's name, logo, theme and copy. The
 * `domain` the brands endpoint returns is compared against the host being served instead.
 *
 * Reading `window.location` here is not pure, but the host cannot change without a document load,
 * which rehydrates the store — so memoising on `state.brand` is still correct.
 */
export const selectIsBrandDetailsExist = createSelector(
  [selectBrandState],
  (brandState) => !!brandState.id && brandState.domain === getBrandDomain(),
);

export const selectBrandVaultProject = createSelector(
  [selectBrandCode],
  getBrandVaultProject,
);

export const selectBrandLogoUrl = createSelector(
  [selectBrandState],
  (brandState) => brandState.logoUrl,
);

export const selectBrandFaviconUrl = createSelector(
  [selectBrandState],
  (brandState) => brandState.faviconUrl,
);

export const selectBrandCustomerServiceEmail = createSelector(
  [selectBrandState],
  (brandState) => brandState.customerServiceEmail,
);
